import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { SampleSlice } from "../../../../src/lib/audio";
import {
  addSliceAt,
  nudgeSlice,
  removeSlice,
  sortedSlices,
  toggleSliceOmit,
} from "../../lib/samplerSlices";
import {
  buildPeakPyramid,
  clientXToFrame,
  columnsFromPeaks,
  normalizeLoopRange,
  normalizeRegion,
  pickPeakLevel,
  type PeakPyramid,
} from "../../lib/wavePeaks";

export type WaveRegion = { start: number; end: number };
export type WaveLoop = { enabled: boolean; start: number; end: number };

export type WaveformEditorProps = {
  pcm: Float32Array | null;
  sampleRate: number;
  region: WaveRegion;
  loop: WaveLoop;
  reverse?: boolean;
  playhead?: number | null;
  slices?: SampleSlice[];
  selectedSliceId?: string | null;
  onRegionChange: (region: WaveRegion) => void;
  onLoopChange: (loop: WaveLoop) => void;
  onSlicesChange?: (slices: SampleSlice[]) => void;
  onSelectSlice?: (id: string | null) => void;
  className?: string;
  emptyLabel?: string;
};

type DragMode =
  | { kind: "select"; anchor: number }
  | { kind: "pan"; startX: number; viewStart: number; viewEnd: number }
  | { kind: "loopStart" }
  | { kind: "loopEnd" }
  | { kind: "slice"; id: string }
  | { kind: "overview" };

const WAVE_H = 160;
const OVERVIEW_H = 28;
const HANDLE_HIT = 8;
const SLICE_HIT = 6;

/**
 * Canvas waveform + DOM loop braces. Zoom (wheel), pan (shift-drag / middle),
 * selection drag, loop handles, overview scrub.
 */
export function WaveformEditor({
  pcm,
  sampleRate,
  region,
  loop,
  reverse = false,
  playhead = null,
  slices,
  selectedSliceId = null,
  onRegionChange,
  onLoopChange,
  onSlicesChange,
  onSelectSlice,
  className = "",
  emptyLabel = "Import a sample or pick a factory cycle",
}: WaveformEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const length = pcm?.length ?? 0;

  const [viewStart, setViewStart] = useState(0);
  const [viewEnd, setViewEnd] = useState(1);
  const dragRef = useRef<DragMode | null>(null);
  const [hoverFrame, setHoverFrame] = useState<number | null>(null);

  const pyramid = useMemo<PeakPyramid | null>(
    () => (pcm && pcm.length > 0 ? buildPeakPyramid(pcm) : null),
    [pcm],
  );

  // Reset view when sample changes.
  useEffect(() => {
    setViewStart(0);
    setViewEnd(Math.max(1, length));
  }, [length, pcm]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const ro = new ResizeObserver(entries => {
      const w = Math.floor(entries[0]?.contentRect.width ?? 0);
      if (w > 0) {
        setWidth(w);
      }
    });
    ro.observe(el);
    setWidth(Math.floor(el.clientWidth) || 640);
    return () => ro.disconnect();
  }, []);

  const setView = useCallback((start: number, end: number) => {
    if (length <= 0) {
      setViewStart(0);
      setViewEnd(1);
      return;
    }
    let a = Math.max(0, Math.min(length, start));
    let b = Math.max(0, Math.min(length, end));
    if (b <= a) {
      b = Math.min(length, a + 1);
    }
    const minSpan = Math.max(32, length * 0.0005);
    if (b - a < minSpan) {
      const mid = (a + b) / 2;
      a = Math.max(0, mid - minSpan / 2);
      b = Math.min(length, a + minSpan);
    }
    setViewStart(a);
    setViewEnd(b);
  }, [length]);

  const zoomAt = useCallback((clientX: number, factor: number) => {
    const el = canvasRef.current;
    if (!el || length <= 0) {
      return;
    }
    const focus = clientXToFrame(clientX, el, viewStart, viewEnd);
    const span = viewEnd - viewStart;
    const nextSpan = Math.max(32, Math.min(length, span * factor));
    const t = span > 0 ? (focus - viewStart) / span : 0.5;
    let nextStart = focus - t * nextSpan;
    let nextEnd = nextStart + nextSpan;
    if (nextStart < 0) {
      nextStart = 0;
      nextEnd = nextSpan;
    }
    if (nextEnd > length) {
      nextEnd = length;
      nextStart = Math.max(0, length - nextSpan);
    }
    setView(nextStart, nextEnd);
  }, [length, setView, viewEnd, viewStart]);

  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const totalH = WAVE_H + OVERVIEW_H + 8;
    if (canvas.width !== Math.floor(width * dpr) || canvas.height !== Math.floor(totalH * dpr)) {
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(totalH * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${totalH}px`;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, totalH);

    // Background
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, width, WAVE_H);

    if (!pyramid || length <= 0 || width <= 0) {
      ctx.fillStyle = "#64748b";
      ctx.font = "12px ui-sans-serif, system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(emptyLabel, width / 2, WAVE_H / 2);
      return;
    }

    const level = pickPeakLevel(pyramid, viewStart, viewEnd, width);
    if (!level) {
      return;
    }
    const cols = columnsFromPeaks(level, viewStart, viewEnd, width);
    const mid = WAVE_H / 2;
    const amp = WAVE_H * 0.42;

    // Selection / region fill
    const r = normalizeRegion(region.start, region.end, length);
    const rx0 = frameToX(r.start, viewStart, viewEnd, width);
    const rx1 = frameToX(r.end, viewStart, viewEnd, width);
    ctx.fillStyle = "rgba(61, 139, 253, 0.16)";
    ctx.fillRect(Math.min(rx0, rx1), 0, Math.abs(rx1 - rx0), WAVE_H);

    // Waveform
    ctx.strokeStyle = reverse ? "#f0abfc" : "#7dd3fc";
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = 0; x < cols.length; x++) {
      const c = cols[x]!;
      const y0 = mid - c.max * amp;
      const y1 = mid - c.min * amp;
      ctx.moveTo(x + 0.5, y0);
      ctx.lineTo(x + 0.5, y1);
    }
    ctx.stroke();

    // Zero line
    ctx.strokeStyle = "rgba(148, 163, 184, 0.25)";
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(width, mid);
    ctx.stroke();

    // Loop braces (vertical lines; handles are DOM)
    if (loop.enabled) {
      const lr = normalizeLoopRange(loop.start, loop.end, r.start, r.end || length);
      const lx0 = frameToX(lr.start, viewStart, viewEnd, width);
      const lx1 = frameToX(lr.end, viewStart, viewEnd, width);
      ctx.fillStyle = "rgba(52, 211, 153, 0.10)";
      ctx.fillRect(Math.min(lx0, lx1), 0, Math.abs(lx1 - lx0), WAVE_H);
      ctx.strokeStyle = "#34d399";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(lx0 + 0.5, 0);
      ctx.lineTo(lx0 + 0.5, WAVE_H);
      ctx.moveTo(lx1 + 0.5, 0);
      ctx.lineTo(lx1 + 0.5, WAVE_H);
      ctx.stroke();
    }

    // Slice markers — right triangle = explode; left + dim = omit from explode
    for (const slice of sortedSlices(slices)) {
      const sx = frameToX(slice.frame, viewStart, viewEnd, width);
      const selected = slice.id === selectedSliceId;
      const omit = slice.omit === true;
      ctx.strokeStyle = selected
        ? (omit ? "#94a3b8" : "#fbbf24")
        : (omit ? "#64748b" : "#fb923c");
      ctx.lineWidth = selected ? 2 : 1;
      ctx.globalAlpha = omit && !selected ? 0.72 : 1;
      ctx.beginPath();
      ctx.moveTo(sx + 0.5, 0);
      ctx.lineTo(sx + 0.5, WAVE_H);
      ctx.stroke();
      ctx.fillStyle = selected
        ? (omit ? "#94a3b8" : "#fbbf24")
        : (omit ? "#64748b" : "#fb923c");
      ctx.beginPath();
      if (omit) {
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx - 5, 0);
        ctx.lineTo(sx, 8);
      } else {
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx + 5, 0);
        ctx.lineTo(sx, 8);
      }
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Playhead
    if (playhead != null && Number.isFinite(playhead)) {
      const px = frameToX(playhead, viewStart, viewEnd, width);
      ctx.strokeStyle = "#fde68a";
      ctx.lineWidth = 1.25;
      ctx.beginPath();
      ctx.moveTo(px + 0.5, 0);
      ctx.lineTo(px + 0.5, WAVE_H);
      ctx.stroke();
    }

    // Hover
    if (hoverFrame != null) {
      const hx = frameToX(hoverFrame, viewStart, viewEnd, width);
      ctx.strokeStyle = "rgba(226, 232, 240, 0.35)";
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(hx + 0.5, 0);
      ctx.lineTo(hx + 0.5, WAVE_H);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Overview
    const oy = WAVE_H + 8;
    ctx.fillStyle = "#080e18";
    ctx.fillRect(0, oy, width, OVERVIEW_H);
    const overviewLevel = pickPeakLevel(pyramid, 0, length, width) ?? level;
    const ovCols = columnsFromPeaks(overviewLevel, 0, length, width);
    const omid = oy + OVERVIEW_H / 2;
    const oamp = OVERVIEW_H * 0.38;
    ctx.strokeStyle = "#475569";
    ctx.beginPath();
    for (let x = 0; x < ovCols.length; x++) {
      const c = ovCols[x]!;
      ctx.moveTo(x + 0.5, omid - c.max * oamp);
      ctx.lineTo(x + 0.5, omid - c.min * oamp);
    }
    ctx.stroke();
    const ox0 = (viewStart / length) * width;
    const ox1 = (viewEnd / length) * width;
    ctx.fillStyle = "rgba(61, 139, 253, 0.22)";
    ctx.fillRect(ox0, oy, Math.max(2, ox1 - ox0), OVERVIEW_H);
    ctx.strokeStyle = "rgba(61, 139, 253, 0.7)";
    ctx.strokeRect(ox0 + 0.5, oy + 0.5, Math.max(2, ox1 - ox0) - 1, OVERVIEW_H - 1);

    void sampleRate;
  }, [
    emptyLabel,
    hoverFrame,
    length,
    loop,
    playhead,
    pyramid,
    region,
    reverse,
    sampleRate,
    selectedSliceId,
    slices,
    viewEnd,
    viewStart,
    width,
  ]);

  useEffect(() => {
    paint();
  }, [paint]);

  function hitSlice(clientX: number): string | null {
    if (!canvasRef.current || length <= 0 || !slices?.length) {
      return null;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    let best: { id: string; dist: number } | null = null;
    for (const slice of slices) {
      const sx = frameToX(slice.frame, viewStart, viewEnd, width);
      const dist = Math.abs(x - sx);
      if (dist <= SLICE_HIT && (!best || dist < best.dist)) {
        best = { id: slice.id, dist };
      }
    }
    return best?.id ?? null;
  }

  function hitLoopHandle(clientX: number): "loopStart" | "loopEnd" | null {
    if (!loop.enabled || !canvasRef.current || length <= 0) {
      return null;
    }
    const r = normalizeRegion(region.start, region.end, length);
    const lr = normalizeLoopRange(loop.start, loop.end, r.start, r.end || length);
    const x0 = frameToX(lr.start, viewStart, viewEnd, width);
    const x1 = frameToX(lr.end, viewStart, viewEnd, width);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    if (Math.abs(x - x0) <= HANDLE_HIT) {
      return "loopStart";
    }
    if (Math.abs(x - x1) <= HANDLE_HIT) {
      return "loopEnd";
    }
    return null;
  }

  function onPointerDown(event: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!pcm || length <= 0 || !canvasRef.current) {
      return;
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const localY = event.clientY - rect.top;

    // Overview strip
    if (localY >= WAVE_H + 4) {
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      dragRef.current = { kind: "overview" };
      scrubOverview(event.clientX);
      return;
    }

    if (event.button === 1 || event.shiftKey) {
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      dragRef.current = {
        kind: "pan",
        startX: event.clientX,
        viewStart,
        viewEnd,
      };
      return;
    }

    if (event.button !== 0) {
      return;
    }

    const handle = hitLoopHandle(event.clientX);
    if (handle) {
      event.preventDefault();
      canvas.setPointerCapture(event.pointerId);
      dragRef.current = { kind: handle };
      return;
    }

    const sliceId = hitSlice(event.clientX);
    if (sliceId && onSlicesChange) {
      event.preventDefault();
      onSelectSlice?.(sliceId);
      canvas.setPointerCapture(event.pointerId);
      dragRef.current = { kind: "slice", id: sliceId };
      return;
    }

    event.preventDefault();
    onSelectSlice?.(null);
    canvas.setPointerCapture(event.pointerId);
    const frame = clientXToFrame(event.clientX, canvas, viewStart, viewEnd);
    dragRef.current = { kind: "select", anchor: frame };
    onRegionChange(normalizeRegion(frame, frame + 1, length));
  }

  function scrubOverview(clientX: number): void {
    if (!canvasRef.current || length <= 0) {
      return;
    }
    const t = Math.max(0, Math.min(1, (clientX - canvasRef.current.getBoundingClientRect().left) / width));
    const span = viewEnd - viewStart;
    let start = t * length - span / 2;
    let end = start + span;
    if (start < 0) {
      start = 0;
      end = span;
    }
    if (end > length) {
      end = length;
      start = Math.max(0, length - span);
    }
    setView(start, end);
  }

  function onPointerMove(event: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!canvasRef.current || length <= 0) {
      return;
    }
    const frame = clientXToFrame(event.clientX, canvasRef.current, viewStart, viewEnd);
    setHoverFrame(frame);

    const drag = dragRef.current;
    if (!drag) {
      const handle = hitLoopHandle(event.clientX);
      const sliceId = hitSlice(event.clientX);
      canvasRef.current.style.cursor = handle || sliceId
        ? "ew-resize"
        : event.shiftKey
        ? "grab"
        : "crosshair";
      return;
    }

    if (drag.kind === "overview") {
      scrubOverview(event.clientX);
      return;
    }
    if (drag.kind === "pan") {
      const dx = event.clientX - drag.startX;
      const span = drag.viewEnd - drag.viewStart;
      const frames = (-dx / width) * span;
      setView(drag.viewStart + frames, drag.viewEnd + frames);
      return;
    }
    if (drag.kind === "select") {
      onRegionChange(normalizeRegion(drag.anchor, frame, length));
      return;
    }
    if (drag.kind === "slice" && onSlicesChange) {
      onSlicesChange(nudgeSlice(slices, drag.id, frame, length));
      return;
    }
    if (drag.kind === "loopStart" || drag.kind === "loopEnd") {
      const r = normalizeRegion(region.start, region.end, length);
      const cur = normalizeLoopRange(loop.start, loop.end, r.start, r.end || length);
      if (drag.kind === "loopStart") {
        onLoopChange({
          ...loop,
          ...normalizeLoopRange(frame, cur.end, r.start, r.end || length),
        });
      } else {
        onLoopChange({
          ...loop,
          ...normalizeLoopRange(cur.start, frame, r.start, r.end || length),
        });
      }
    }
  }

  function onPointerUp(event: ReactPointerEvent<HTMLCanvasElement>): void {
    if (canvasRef.current?.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId);
    }
    dragRef.current = null;
  }

  // Non-passive wheel so preventDefault actually blocks browser page zoom.
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas) {
      return;
    }
    const handleWheel = (event: WheelEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      if (!pcm || length <= 0) {
        return;
      }
      if (event.ctrlKey || event.metaKey || Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
        const factor = event.deltaY > 0 ? 1.12 : 1 / 1.12;
        zoomAt(event.clientX, factor);
        return;
      }
      const span = viewEnd - viewStart;
      const pan = (event.deltaX / width) * span;
      setView(viewStart + pan, viewEnd + pan);
    };
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    wrap?.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      canvas.removeEventListener("wheel", handleWheel);
      wrap?.removeEventListener("wheel", handleWheel);
    };
  }, [length, pcm, setView, viewEnd, viewStart, width, zoomAt]);

  function onDoubleClick(event: React.MouseEvent<HTMLCanvasElement>): void {
    if (!pcm || length <= 0 || !onSlicesChange || !canvasRef.current) {
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    if (event.clientY - rect.top >= WAVE_H) {
      return;
    }
    const frame = clientXToFrame(event.clientX, canvasRef.current, viewStart, viewEnd);
    const next = addSliceAt(slices, frame);
    onSlicesChange(next);
    const added = next.find(s => Math.abs(s.frame - Math.round(frame)) <= 1);
    if (added) {
      onSelectSlice?.(added.id);
    }
  }

  function onContextMenu(event: React.MouseEvent<HTMLCanvasElement>): void {
    event.preventDefault();
    if (!pcm || length <= 0 || !onSlicesChange || !canvasRef.current) {
      return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    if (event.clientY - rect.top >= WAVE_H) {
      return;
    }
    const sliceId = hitSlice(event.clientX);
    if (!sliceId) {
      return;
    }
    onSelectSlice?.(sliceId);
    onSlicesChange(toggleSliceOmit(slices, sliceId));
  }

  useEffect(() => {
    if (!onSlicesChange) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (!selectedSliceId) {
        return;
      }
      if (event.key === "Delete" || event.key === "Backspace") {
        const tag = (event.target as HTMLElement | null)?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") {
          return;
        }
        event.preventDefault();
        onSlicesChange?.(removeSlice(slices, selectedSliceId));
        onSelectSlice?.(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onSelectSlice, onSlicesChange, selectedSliceId, slices]);

  const r = normalizeRegion(region.start, region.end, length || 1);
  const lr = normalizeLoopRange(loop.start || r.start, loop.end || r.end, r.start, r.end || length || 1);
  const loopStartPct = length > 0 ? ((lr.start - viewStart) / Math.max(1, viewEnd - viewStart)) * 100 : 0;
  const loopEndPct = length > 0 ? ((lr.end - viewStart) / Math.max(1, viewEnd - viewStart)) * 100 : 0;
  const sliceCount = sortedSlices(slices).length;

  return (
    <div ref={wrapRef} className={`relative min-w-0 ${className}`}>
      <canvas
        ref={canvasRef}
        className="block w-full touch-none rounded border border-white/[0.08] bg-[#0b1220]"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
        onPointerLeave={() => setHoverFrame(null)}
      />
      {loop.enabled && length > 0 && (
        <>
          <div
            className="pointer-events-none absolute top-0 h-[160px] w-0.5 bg-emerald-400/90"
            style={{ left: `clamp(0%, ${loopStartPct}%, 100%)` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute top-0 h-[160px] w-0.5 bg-emerald-400/90"
            style={{ left: `clamp(0%, ${loopEndPct}%, 100%)` }}
            aria-hidden
          />
        </>
      )}
      <div className="mt-1 flex flex-wrap gap-3 text-[10px] text-lab-muted">
        <span>
          View {fmtFrames(viewStart, sampleRate)} – {fmtFrames(viewEnd, sampleRate)}
        </span>
        <span>
          Region {fmtFrames(r.start, sampleRate)} – {fmtFrames(r.end, sampleRate)}
        </span>
        {loop.enabled && (
          <span>
            Loop {fmtFrames(lr.start, sampleRate)} – {fmtFrames(lr.end, sampleRate)}
          </span>
        )}
        {sliceCount > 0 && (
          <span>{sliceCount} slice{sliceCount === 1 ? "" : "s"}</span>
        )}
        <span className="text-lab-muted/70">
          Wheel zoom · Shift-pan · Drag select · Dbl-click slice · Right-click omit · Del removes
        </span>
      </div>
    </div>
  );
}

function frameToX(frame: number, viewStart: number, viewEnd: number, width: number): number {
  const span = Math.max(1e-9, viewEnd - viewStart);
  return ((frame - viewStart) / span) * width;
}

function fmtFrames(frame: number, sampleRate: number): string {
  if (sampleRate <= 0) {
    return `${Math.round(frame)}f`;
  }
  const sec = frame / sampleRate;
  if (sec < 1) {
    return `${(sec * 1000).toFixed(0)}ms`;
  }
  return `${sec.toFixed(2)}s`;
}

/** Tiny static waveform peek for the dock (non-interactive). */
export function WaveformThumbnail({
  pcm,
  className = "",
}: {
  pcm: Float32Array | null;
  className?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const w = 120;
    const h = 28;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#0b1220";
    ctx.fillRect(0, 0, w, h);
    if (!pcm || pcm.length === 0) {
      ctx.fillStyle = "#475569";
      ctx.fillRect(0, h / 2 - 0.5, w, 1);
      return;
    }
    const pyramid = buildPeakPyramid(pcm, 6);
    const level = pickPeakLevel(pyramid, 0, pcm.length, w);
    if (!level) {
      return;
    }
    const cols = columnsFromPeaks(level, 0, pcm.length, w);
    const mid = h / 2;
    const amp = h * 0.4;
    ctx.strokeStyle = "#7dd3fc";
    ctx.beginPath();
    for (let x = 0; x < cols.length; x++) {
      const c = cols[x]!;
      ctx.moveTo(x + 0.5, mid - c.max * amp);
      ctx.lineTo(x + 0.5, mid - c.min * amp);
    }
    ctx.stroke();
  }, [pcm]);

  return (
    <canvas
      ref={canvasRef}
      className={`rounded border border-white/[0.08] ${className}`}
      aria-hidden
    />
  );
}
